import { useEffect, useMemo, useState } from 'react';
import { Button, EmptyState, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, money, qty as fmtQty } from '../lib/format.ts';
import { useAction, useSave } from './manage/common.tsx';
import { parseQty, withUnit, type Unit } from './manage/Stock.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * The planner: what to make and what to buy so nothing runs short, worked out from open orders,
 * confirmed sales and running-low levels. Proposals become planned production orders and draft
 * purchase orders only when someone accepts them.
 */

interface Proposal {
  kind: 'make' | 'buy';
  item: string;
  name: string;
  unit: Unit;
  qty: number;
  on_hand: number;
  incoming: number;
  needs: { sales: number; production: number; planned: number; min: number };
  unit_cost: number;
  value: number;
  bom: { id: string; name: string; output_qty: number } | null;
  supplier: { id: string; name: string } | null;
  supplier_name: string | null;
  needed_by: string;
}
interface Plan {
  proposals: Proposal[];
  today: string;
  suppliers: { id: string; name: string }[] | null;
  can_buy: boolean;
  can_make: boolean;
}
interface Pick {
  on: boolean;
  qty: string;
  supplier: string;
}

/** Why an item is proposed, in a few words: "Sales 4 kg · Production 6 kg · Keep 3 kg". */
function why(p: Proposal) {
  const parts: string[] = [];
  if (p.needs.sales) parts.push(t('plan.why_sales', { qty: withUnit(p.needs.sales, p.unit) }));
  if (p.needs.production + p.needs.planned) parts.push(t('plan.why_production', { qty: withUnit(p.needs.production + p.needs.planned, p.unit) }));
  if (p.needs.min) parts.push(t('plan.why_min', { qty: withUnit(p.needs.min, p.unit) }));
  parts.push(t('plan.why_have', { qty: withUnit(p.on_hand, p.unit) }));
  if (p.incoming) parts.push(t('plan.why_coming', { qty: withUnit(p.incoming, p.unit) }));
  return parts.join(' · ');
}

export function PlannerTab({ onDone }: { onDone: () => Promise<void> }) {
  const { data, reload } = useAction<Plan>('mrp.proposals', {});
  const { busy, run } = useSave();
  const [picks, setPicks] = useState<Record<string, Pick>>({});
  useEffect(() => {
    if (!data) return;
    // Items to buy without a known supplier start unticked: someone picks the supplier first.
    setPicks(Object.fromEntries(data.proposals.map((p) => [p.item, { on: p.kind === 'make' ? data.can_make : data.can_buy && !!p.supplier, qty: fmtQty(p.qty), supplier: p.supplier?.id ?? '' }])));
  }, [data]);
  const chosen = useMemo(
    () =>
      (data?.proposals ?? [])
        .map((p) => ({ p, pick: picks[p.item], qty: parseQty(picks[p.item]?.qty ?? '') }))
        .filter((x) => x.pick?.on),
    [data, picks],
  );
  if (!data) return <Skeleton h={260} r="var(--radius-lg)" />;
  const make = chosen.filter((c) => c.p.kind === 'make').length;
  const buy = chosen.filter((c) => c.p.kind === 'buy');
  const suppliers = new Set(buy.map((c) => c.pick!.supplier).filter(Boolean)).size;
  const bad = chosen.some((c) => !c.qty || c.qty <= 0 || (c.p.kind === 'buy' && !c.pick!.supplier));
  const value = chosen.reduce((s, c) => s + Math.round((c.qty ?? 0) * c.p.unit_cost), 0);
  const set = (item: string, patch: Partial<Pick>) => setPicks({ ...picks, [item]: { ...picks[item]!, ...patch } });
  const accept = () =>
    void run(
      async () => {
        await action('mrp.accept_proposals', {
          lines: chosen.map((c) => ({ kind: c.p.kind, item: c.p.item, qty: c.qty, date: c.p.needed_by, supplier: c.p.kind === 'buy' ? c.pick!.supplier : null })),
        });
        await Promise.all([reload(), onDone()]);
      },
      [make ? tn('plan.made_orders', make) : null, suppliers ? tn('plan.made_purchases', suppliers) : null].filter(Boolean).join(' · '),
    );

  return (
    <section className="panel planner">
      <div className="prot-history-head mrp-section-head">
        <div>
          <h2 className="rep-sub">{t('plan.title')}</h2>
          <p className="muted small">{t('plan.hint')}</p>
        </div>
        <Button icon="repeat" onClick={() => void reload()}>
          {t('plan.refresh')}
        </Button>
      </div>
      {!data.proposals.length ? (
        <EmptyState icon="check" title={t('plan.none')} body={t('plan.none_body')} />
      ) : (
        <>
          <div className="rep-kpis planner-kpis">
            <Kpi label={t('plan.k_make')} value={String(data.proposals.filter((p) => p.kind === 'make').length)} hint={t('plan.k_make_hint')} />
            <Kpi label={t('plan.k_buy')} value={String(data.proposals.filter((p) => p.kind === 'buy').length)} hint={t('plan.k_buy_hint')} />
            <Kpi label={t('plan.k_value')} value={money(data.proposals.reduce((s, p) => s + p.value, 0))} hint={t('plan.k_value_hint')} />
          </div>
          <Table
            head={['', t('plan.c_item'), t('plan.c_do'), t('plan.c_qty'), t('plan.c_by'), t('plan.c_from'), t('plan.c_value')]}
            left={[1, 2, 5]}
            phoneHide={[4, 6]}
            sortable={false}
            rows={data.proposals.map((p) => {
              const pick = picks[p.item];
              const allowed = p.kind === 'make' ? data.can_make : data.can_buy;
              const qty = parseQty(pick?.qty ?? '');
              return {
                key: p.item,
                cells: [
                  <input
                    type="checkbox"
                    className="planner-check"
                    checked={!!pick?.on}
                    disabled={!allowed}
                    aria-label={t('plan.pick', { name: p.name })}
                    onChange={(e) => set(p.item, { on: e.target.checked })}
                  />,
                  <span className="vat-party">
                    <span className="strong">{p.name}</span>
                    <span className="muted">{why(p)}</span>
                  </span>,
                  p.kind === 'make' ? (
                    <StatusBadge tone="info" icon="factory" label={p.bom ? t('plan.make_with', { bom: p.bom.name }) : t('plan.make')} />
                  ) : (
                    <StatusBadge tone="neutral" icon="cart" label={t('plan.buy')} />
                  ),
                  <span className="input-unit planner-qty">
                    <input
                      className={`input${pick?.qty && !qty ? ' invalid' : ''}`}
                      inputMode="decimal"
                      aria-label={t('plan.qty_for', { name: p.name })}
                      value={pick?.qty ?? ''}
                      disabled={!allowed}
                      onChange={(e) => set(p.item, { qty: e.target.value })}
                    />
                    <span>{t(`stock.unit_${p.unit}`)}</span>
                  </span>,
                  <span className={p.needed_by <= data.today ? 'warn strong' : ''}>{p.needed_by <= data.today ? t('plan.now') : day(p.needed_by)}</span>,
                  p.kind === 'buy' ? (
                    data.suppliers ? (
                      <select
                        className={`input planner-supplier${pick?.on && !pick.supplier ? ' invalid' : ''}`}
                        value={pick?.supplier ?? ''}
                        disabled={!allowed}
                        aria-label={t('plan.supplier_for', { name: p.name })}
                        onChange={(e) => set(p.item, { supplier: e.target.value, ...(e.target.value ? { on: true } : {}) })}
                      >
                        <option value="">{p.supplier_name ? t('plan.not_a_supplier', { name: p.supplier_name }) : t('plan.pick_supplier')}</option>
                        {data.suppliers.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="muted">{p.supplier_name ?? '—'}</span>
                    )
                  ) : (
                    <span className="muted">{t('plan.in_house')}</span>
                  ),
                  <span className="tabular">{money(Math.round((qty ?? 0) * p.unit_cost))}</span>,
                ],
              };
            })}
          />
          <div className="planner-foot">
            <span className="muted">
              {chosen.length
                ? [make ? tn('plan.will_make', make) : null, suppliers ? tn('plan.will_buy', suppliers) : null, money(value)].filter(Boolean).join(' · ')
                : t('plan.pick_some')}
            </span>
            <Button variant="primary" icon="check" onClick={accept} busy={busy} disabled={!chosen.length || bad}>
              {t('plan.accept')}
            </Button>
          </div>
          {!data.can_buy && data.proposals.some((p) => p.kind === 'buy') ? <p className="muted small">{data.suppliers ? t('plan.no_buy_right') : t('plan.no_purchasing')}</p> : null}
        </>
      )}
    </section>
  );
}
